import {
  albumTrackCountLabel,
  albumTypeLabel,
  type AlbumReleaseType,
} from "@/lib/utils";
import { Flex, Text } from "@radix-ui/themes";
import { LuCalendar } from "react-icons/lu";
import { MdOutlineAlbum } from "react-icons/md";
import { parseISO } from "date-fns";
import GenresIcon from "@/assets/icons/Genres.svg?react";

type AlbumDetailsTabProps = {
  description: string | null | undefined;
  genreDisplayText: string | null;
  publishedAt: string | null | undefined;
  releaseDate?: string | null;
  trackCount: number;
  releaseType?: AlbumReleaseType;
  credits: string | null | undefined;
};

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "-";
  return parseISO(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateTime(dateString: string | null | undefined): string {
  if (!dateString) return "-";
  return parseISO(dateString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

export default function AlbumDetailsTab({
  description,
  genreDisplayText,
  publishedAt,
  releaseDate,
  trackCount,
  releaseType,
  credits,
}: AlbumDetailsTabProps) {
  return (
    <Flex direction="column" className="gap-6">
      <div
        className="w-full h-[1px]"
        style={{ backgroundColor: "rgba(255, 255, 255, 0.15)" }}
      />
      {/* About this Album */}
      {description && (
        <Flex direction="column" className="gap-2">
          <Text className="text-[16px] font-semibold leading-[140%] text-white">
            About this {albumTypeLabel({ releaseType, trackCount })}
          </Text>
          <Text className="text-[12px] font-normal leading-[140%] text-white/80 whitespace-pre-wrap">
            {description}
          </Text>
        </Flex>
      )}

      {/* Metadata Rows */}
      <Flex direction="column" className="gap-3">
        {genreDisplayText && (
          <Flex className="items-center gap-3">
            <GenresIcon className="w-5 h-5 fill-white/80 shrink-0" />
            <Text className="text-[12px] font-semibold leading-[140%] text-white/80 w-12 shrink-0">
              Genre
            </Text>
            <Text className="text-[12px] font-semibold leading-[140%] text-white">
              {genreDisplayText}
            </Text>
          </Flex>
        )}

        {publishedAt && (
          <Flex className="items-center gap-3">
            <LuCalendar className="w-5 h-5 text-white/80 shrink-0" />
            <Text className="text-[12px] font-semibold leading-[140%] text-white/80 w-12 shrink-0">
              Live
            </Text>
            <Text className="text-[12px] font-semibold leading-[140%] text-white">
              {formatDate(publishedAt)}
            </Text>
          </Flex>
        )}

        {releaseDate && (
          <Flex className="items-center gap-3">
            <LuCalendar className="w-5 h-5 text-white/80 shrink-0" />
            <Text className="text-[12px] font-semibold leading-[140%] text-white/80 w-12 shrink-0">
              Release
            </Text>
            <Text className="text-[12px] font-semibold leading-[140%] text-white">
              {formatDateTime(releaseDate)}
            </Text>
          </Flex>
        )}

        <Flex className="items-center gap-3">
          <MdOutlineAlbum className="w-5 h-5 text-white/80 shrink-0" />
          <Text className="text-[12px] font-semibold leading-[140%] text-white/80 w-12 shrink-0">
            Tracks
          </Text>
          <Text className="text-[12px] font-semibold leading-[140%] text-white">
            {albumTrackCountLabel(
              { releaseType, trackCount },
              { capitalize: true },
            )}
          </Text>
        </Flex>
      </Flex>

      {/* Credits */}
      {credits && (
        <Flex direction="column" className="gap-2">
          <Text className="text-[12px] font-semibold leading-[140%] text-white">
            Credits
          </Text>
          <Text className="text-[12px] font-normal leading-[140%] text-white/80 whitespace-pre-wrap">
            {credits}
          </Text>
        </Flex>
      )}
    </Flex>
  );
}
